import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function Friends({ onChatOpen }) {
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [searchUserId, setSearchUserId] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    getCurrentUser();
    loadFriends();
    loadFriendRequests();
    loadSentRequests();
  }, []);

  const getCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
      
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        setCurrentProfile(profile);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFriends = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('Loading friends for user:', user.id);

      // First, get accepted friendships where user is involved
      const { data: friendshipData, error: friendshipError } = await supabase
        .from('friendships')
        .select('*')
        .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
        .eq('status', 'accepted');

      if (friendshipError) {
        console.error('Error loading friendships:', friendshipError);
        throw friendshipError;
      }

      console.log('Raw accepted friendships:', friendshipData);

      if (!friendshipData || friendshipData.length === 0) {
        console.log('No accepted friendships found');
        setFriends([]);
        return;
      }

      // Get friend user IDs (the other person in each friendship)
      const friendIds = friendshipData.map(friendship => {
        return friendship.requester_id === user.id 
          ? friendship.addressee_id 
          : friendship.requester_id;
      });

      console.log('Friend IDs to load:', friendIds);

      // Get profile data for all friends
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', friendIds);

      if (profileError) {
        console.error('Error loading friend profiles:', profileError);
        // Still show friends even if we can't load all profile data
        const friendsWithoutProfiles = friendshipData.map(friendship => {
          const friendId = friendship.requester_id === user.id 
            ? friendship.addressee_id 
            : friendship.requester_id;
          return {
            id: friendId,
            name: 'Unknown User',
            photo_url: null,
            location: 'Unknown',
            friendship_id: friendship.id,
            friendship_date: friendship.updated_at
          };
        });
        setFriends(friendsWithoutProfiles);
        return;
      }

      // Combine friendship data with profile data
      const friendsList = friendshipData.map(friendship => {
        const friendId = friendship.requester_id === user.id 
          ? friendship.addressee_id 
          : friendship.requester_id;
        
        const friendProfile = profileData.find(p => p.id === friendId);
        
        return {
          ...(friendProfile || { 
            id: friendId, 
            name: 'Unknown User', 
            photo_url: null,
            location: 'Unknown'
          }),
          friendship_id: friendship.id,
          friendship_date: friendship.updated_at
        };
      });

      console.log('Friends loaded:', friendsList);
      setFriends(friendsList);
    } catch (error) {
      console.error('Error loading friends:', error);
    }
  };

  const loadFriendRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('Loading friend requests for user:', user.id);

      // First, get the basic friendship data
      const { data: friendshipData, error: friendshipError } = await supabase
        .from('friendships')
        .select('*')
        .eq('addressee_id', user.id)
        .eq('status', 'pending');

      if (friendshipError) {
        console.error('Error loading friendships:', friendshipError);
        throw friendshipError;
      }

      console.log('Raw friend request data:', friendshipData);

      if (!friendshipData || friendshipData.length === 0) {
        console.log('No friend requests found');
        setFriendRequests([]);
        return;
      }

      // Then get the profile data for requesters
      const requesterIds = friendshipData.map(f => f.requester_id);
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', requesterIds);

      if (profileError) {
        console.error('Error loading requester profiles:', profileError);
        // Still show the requests even if we can't load profile data
        const requestsWithoutProfiles = friendshipData.map(f => ({
          ...f,
          requester: { 
            id: f.requester_id, 
            name: 'Unknown User', 
            photo_url: null,
            location: 'Unknown'
          }
        }));
        setFriendRequests(requestsWithoutProfiles);
        return;
      }

      // Combine friendship data with profile data
      const combinedRequests = friendshipData.map(friendship => {
        const requesterProfile = profileData.find(p => p.id === friendship.requester_id);
        return {
          ...friendship,
          requester: requesterProfile || { 
            id: friendship.requester_id, 
            name: 'Unknown User', 
            photo_url: null,
            location: 'Unknown'
          }
        };
      });

      console.log('Friend requests loaded:', combinedRequests);
      setFriendRequests(combinedRequests);
    } catch (error) {
      console.error('Error loading friend requests:', error);
    }
  };

  const loadSentRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('Loading sent requests for user:', user.id);

      // First, get the basic friendship data
      const { data: friendshipData, error: friendshipError } = await supabase
        .from('friendships')
        .select('*')
        .eq('requester_id', user.id)
        .eq('status', 'pending');

      if (friendshipError) {
        console.error('Error loading friendships:', friendshipError);
        throw friendshipError;
      }

      console.log('Raw friendship data:', friendshipData);

      if (!friendshipData || friendshipData.length === 0) {
        console.log('No sent requests found');
        setSentRequests([]);
        return;
      }

      // Then get the profile data for addressees
      const addresseeIds = friendshipData.map(f => f.addressee_id);
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', addresseeIds);

      if (profileError) {
        console.error('Error loading profiles:', profileError);
        // Still show the requests even if we can't load profile data
        const requestsWithoutProfiles = friendshipData.map(f => ({
          ...f,
          addressee: { 
            id: f.addressee_id, 
            name: 'Unknown User', 
            photo_url: null,
            location: 'Unknown'
          }
        }));
        setSentRequests(requestsWithoutProfiles);
        return;
      }

      // Combine friendship data with profile data
      const combinedRequests = friendshipData.map(friendship => {
        const addresseeProfile = profileData.find(p => p.id === friendship.addressee_id);
        return {
          ...friendship,
          addressee: addresseeProfile || { 
            id: friendship.addressee_id, 
            name: 'Unknown User', 
            photo_url: null,
            location: 'Unknown'
          }
        };
      });

      console.log('Sent requests loaded:', combinedRequests);
      setSentRequests(combinedRequests);
    } catch (error) {
      console.error('Error loading sent requests:', error);
    }
  };

  const searchUserById = async () => {
    if (!searchUserId.trim()) return;
    
    setSearchLoading(true);
    setSearchResult(null);

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', searchUserId.trim())
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          setSearchResult({ error: 'User not found' });
        } else {
          throw error;
        }
        return;
      }

      if (profile.id === currentUser.id) {
        setSearchResult({ error: 'You cannot add yourself as a friend' });
        return;
      }

      // Check if already friends or request exists
      const { data: existingRelation } = await supabase
        .from('friendships')
        .select('*')
        .or(
          `and(requester_id.eq.${currentUser.id},addressee_id.eq.${profile.id}),and(requester_id.eq.${profile.id},addressee_id.eq.${currentUser.id})`
        )
        .single();

      if (existingRelation) {
        if (existingRelation.status === 'accepted') {
          setSearchResult({ error: 'You are already friends with this user' });
        } else if (existingRelation.status === 'pending') {
          setSearchResult({ error: 'Friend request already sent or received' });
        } else {
          setSearchResult({ error: 'Cannot send friend request to this user' });
        }
        return;
      }

      setSearchResult({ profile });
    } catch (error) {
      console.error('Error searching user:', error);
      setSearchResult({ error: 'Error searching for user' });
    } finally {
      setSearchLoading(false);
    }
  };

  const sendFriendRequest = async (targetUserId) => {
    setRequestLoading(true);
    console.log('Sending friend request from:', currentUser.id, 'to:', targetUserId);

    try {
      const { data, error } = await supabase
        .from('friendships')
        .insert({
          requester_id: currentUser.id,
          addressee_id: targetUserId,
          status: 'pending'
        })
        .select();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Friend request inserted:', data);
      alert('Friend request sent successfully!');
      setSearchResult(null);
      setSearchUserId('');
      await loadSentRequests(); // Ensure we wait for the reload
    } catch (error) {
      console.error('Error sending friend request:', error);
      alert(`Error sending friend request: ${error.message}`);
    } finally {
      setRequestLoading(false);
    }
  };

  const respondToFriendRequest = async (friendshipId, response) => {
    try {
      console.log(`${response === 'accept' ? 'Accepting' : 'Declining'} friend request:`, friendshipId);

      if (response === 'accept') {
        const { data, error } = await supabase
          .from('friendships')
          .update({ 
            status: 'accepted',
            updated_at: new Date().toISOString()
          })
          .eq('id', friendshipId)
          .select();

        if (error) {
          console.error('Error accepting friend request:', error);
          throw error;
        }
        
        console.log('Friend request accepted successfully:', data);
        alert('Friend request accepted!');
      } else {
        const { error } = await supabase
          .from('friendships')
          .update({ status: 'rejected' })
          .eq('id', friendshipId);

        if (error) throw error;
        alert('Friend request declined');
      }

      // Reload all data to reflect changes
      console.log('Reloading all friend data after response...');
      await loadFriendRequests();
      await loadFriends();
      await loadSentRequests();
    } catch (error) {
      console.error('Error responding to friend request:', error);
      alert(`Error processing friend request: ${error.message}`);
    }
  };

  const cancelFriendRequest = async (friendshipId) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('id', friendshipId);

      if (error) throw error;
      
      alert('Friend request cancelled');
      loadSentRequests();
    } catch (error) {
      console.error('Error cancelling friend request:', error);
      alert('Error cancelling friend request');
    }
  };

  const removeFriend = async (friendshipId) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) return;

    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('id', friendshipId);

      if (error) throw error;
      
      alert('Friend removed');
      loadFriends();
    } catch (error) {
      console.error('Error removing friend:', error);
      alert('Error removing friend');
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        color: 'white',
        fontSize: '18px'
      }}>
        Loading friends...
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px 0'
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '30px',
          paddingTop: '20px'
        }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>
            Friends
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px',
            margin: 0
          }}>
            Connect with friends by their User ID
          </p>
        </div>

        {/* Your User ID Display */}
        {currentProfile?.user_id && (
          <div style={{
            background: 'rgba(0, 233, 255, 0.1)',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '30px',
            border: '1px solid rgba(0, 233, 255, 0.3)',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#00E9FF',
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '15px',
              margin: 0
            }}>
              🆔 Your User ID
            </h3>
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              padding: '15px',
              fontSize: '24px',
              fontWeight: '700',
              color: '#00E9FF',
              letterSpacing: '2px',
              fontFamily: 'monospace',
              marginBottom: '15px'
            }}>
              {currentProfile.user_id}
            </div>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              margin: 0
            }}>
              Share this ID with friends so they can add you
            </p>
          </div>
        )}

        {/* Add Friend Section */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '20px',
          padding: '25px',
          marginBottom: '30px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{
            color: '#00E9FF',
            fontSize: '20px',
            fontWeight: '600',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            ➕ Add Friend
          </h3>
          
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <input
              type="text"
              value={searchUserId}
              onChange={(e) => setSearchUserId(e.target.value)}
              placeholder="Enter User ID"
              style={{
                flex: 1,
                padding: '15px 20px',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
              onKeyPress={(e) => e.key === 'Enter' && searchUserById()}
            />
            <button
              onClick={searchUserById}
              disabled={searchLoading || !searchUserId.trim()}
              style={{
                padding: '15px 25px',
                background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: searchLoading || !searchUserId.trim() ? 'not-allowed' : 'pointer',
                opacity: searchLoading || !searchUserId.trim() ? 0.6 : 1
              }}
            >
              {searchLoading ? '🔍' : 'Search'}
            </button>
          </div>

          {/* Search Result */}
          {searchResult && (
            <div style={{
              background: searchResult.error ? 'rgba(255, 107, 107, 0.1)' : 'rgba(0, 233, 255, 0.1)',
              border: searchResult.error ? '1px solid rgba(255, 107, 107, 0.3)' : '1px solid rgba(0, 233, 255, 0.3)',
              borderRadius: '15px',
              padding: '20px',
              marginTop: '20px'
            }}>
              {searchResult.error ? (
                <div style={{ color: '#ff6b6b', textAlign: 'center' }}>
                  ❌ {searchResult.error}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <img
                    src={searchResult.profile.photo_url || '/api/placeholder/60/60'}
                    alt={searchResult.profile.name}
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <h4 style={{ color: 'white', margin: 0, marginBottom: '5px' }}>
                      {searchResult.profile.name}
                    </h4>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0, fontSize: '14px' }}>
                      {searchResult.profile.location || 'Location not specified'}
                    </p>
                  </div>
                  <button
                    onClick={() => sendFriendRequest(searchResult.profile.id)}
                    disabled={requestLoading}
                    style={{
                      padding: '10px 20px',
                      background: 'linear-gradient(90deg, #51cf66, #40c057)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: requestLoading ? 'not-allowed' : 'pointer',
                      opacity: requestLoading ? 0.6 : 1
                    }}
                  >
                    {requestLoading ? '⏳' : '➕ Add Friend'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '15px',
          padding: '5px',
          marginBottom: '30px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {[
            { key: 'friends', label: `👥 Friends (${friends.length})`, count: friends.length },
            { key: 'requests', label: `📬 Requests (${friendRequests.length})`, count: friendRequests.length },
            { key: 'sent', label: `📤 Sent (${sentRequests.length})`, count: sentRequests.length }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                padding: '12px 15px',
                background: activeTab === tab.key 
                  ? 'linear-gradient(90deg, #00E9FF, #8A2BE2)' 
                  : 'transparent',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                position: 'relative'
              }}
            >
              {tab.label.split(' (')[0]}
              {tab.count > 0 && (
                <span style={{
                  marginLeft: '5px',
                  background: activeTab === tab.key ? 'rgba(255, 255, 255, 0.2)' : '#ff4757',
                  borderRadius: '10px',
                  padding: '2px 6px',
                  fontSize: '12px'
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content based on active tab */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '20px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          minHeight: '300px'
        }}>
          {activeTab === 'friends' && (
            <div style={{ padding: '25px' }}>
              <h3 style={{
                color: '#00E9FF',
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                👥 Your Friends ({friends.length})
              </h3>
              
              {friends.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.7)',
                  padding: '40px 20px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>👥</div>
                  <p>No friends yet</p>
                  <p style={{ fontSize: '14px' }}>Add friends by searching their User ID above</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {friends.map((friend) => (
                    <div key={friend.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      padding: '15px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '15px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <img
                        src={friend.photo_url || '/api/placeholder/50/50'}
                        alt={friend.name}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <h4 style={{ color: 'white', margin: 0, marginBottom: '5px' }}>
                          {friend.name}
                          {friend.is_verified && (
                            <span style={{ marginLeft: '8px', color: '#28a745' }}>✅</span>
                          )}
                        </h4>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0, fontSize: '14px' }}>
                          {friend.location || 'Location not specified'}
                        </p>
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0, fontSize: '12px' }}>
                          Friends since {new Date(friend.friendship_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {onChatOpen && (
                          <button
                            onClick={() => onChatOpen(friend)}
                            style={{
                              padding: '8px 15px',
                              background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            💬 Chat
                          </button>
                        )}
                        <button
                          onClick={() => removeFriend(friend.friendship_id)}
                          style={{
                            padding: '8px 15px',
                            background: 'rgba(255, 107, 107, 0.2)',
                            color: '#ff6b6b',
                            border: '1px solid rgba(255, 107, 107, 0.3)',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'requests' && (
            <div style={{ padding: '25px' }}>
              <h3 style={{
                color: '#00E9FF',
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                📬 Friend Requests ({friendRequests.length})
              </h3>
              
              {friendRequests.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.7)',
                  padding: '40px 20px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>📬</div>
                  <p>No pending friend requests</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {friendRequests.map((request) => (
                    <div key={request.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      padding: '15px',
                      background: 'rgba(255, 193, 7, 0.1)',
                      borderRadius: '15px',
                      border: '1px solid rgba(255, 193, 7, 0.3)'
                    }}>
                      <img
                        src={request.requester.photo_url || '/api/placeholder/50/50'}
                        alt={request.requester.name}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <h4 style={{ color: 'white', margin: 0, marginBottom: '5px' }}>
                          {request.requester.name}
                          {request.requester.is_verified && (
                            <span style={{ marginLeft: '8px', color: '#28a745' }}>✅</span>
                          )}
                        </h4>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0, fontSize: '14px' }}>
                          {request.requester.location || 'Location not specified'}
                        </p>
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0, fontSize: '12px' }}>
                          Sent {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={() => respondToFriendRequest(request.id, 'accept')}
                          style={{
                            padding: '8px 15px',
                            background: 'linear-gradient(90deg, #51cf66, #40c057)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ✅ Accept
                        </button>
                        <button
                          onClick={() => respondToFriendRequest(request.id, 'reject')}
                          style={{
                            padding: '8px 15px',
                            background: 'rgba(255, 107, 107, 0.2)',
                            color: '#ff6b6b',
                            border: '1px solid rgba(255, 107, 107, 0.3)',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ❌ Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'sent' && (
            <div style={{ padding: '25px' }}>
              <h3 style={{
                color: '#00E9FF',
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                📤 Sent Requests ({sentRequests.length})
              </h3>
              
              {sentRequests.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.7)',
                  padding: '40px 20px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>📤</div>
                  <p>No sent requests</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {sentRequests.map((request) => (
                    <div key={request.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      padding: '15px',
                      background: 'rgba(0, 233, 255, 0.1)',
                      borderRadius: '15px',
                      border: '1px solid rgba(0, 233, 255, 0.3)'
                    }}>
                      <img
                        src={request.addressee.photo_url || '/api/placeholder/50/50'}
                        alt={request.addressee.name}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <h4 style={{ color: 'white', margin: 0, marginBottom: '5px' }}>
                          {request.addressee.name}
                          {request.addressee.is_verified && (
                            <span style={{ marginLeft: '8px', color: '#28a745' }}>✅</span>
                          )}
                        </h4>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0, fontSize: '14px' }}>
                          {request.addressee.location || 'Location not specified'}
                        </p>
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0, fontSize: '12px' }}>
                          Sent {new Date(request.created_at).toLocaleDateString()} • ⏳ Pending
                        </p>
                      </div>
                      <button
                        onClick={() => cancelFriendRequest(request.id)}
                        style={{
                          padding: '8px 15px',
                          background: 'rgba(255, 107, 107, 0.2)',
                          color: '#ff6b6b',
                          border: '1px solid rgba(255, 107, 107, 0.3)',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Friends;